import SignOut from '@mds/icons/SignOut';
import { useCallback, useContext } from 'react';
import { UserForAppBar } from '../../../utils/userName';
import { AccountMenuItemButton, AccountMenuItemButtonProps } from '../AccountMenuItem';
import { AccountMenuContext } from './context';

interface LogoutOptions {
  logoutParams?: {
    returnTo?: string;
  };
}

/**
 * Log out and return to Neon splash page.
 *
 * Intended for Neon apps only.
 */
export function NeonSignOutLink({
  logout,
  user: userProp,
  ...rest
}: {
  /**
   * Auth0 logout method.
   *
   * ```tsx
   * const { logout } = useAuth0()
   * <NeonSignOutLink logout={logout} />
   * ```
   */
  logout: (options?: LogoutOptions) => unknown;
  /**
   * Sign out link is hidden when the user is undefined.
   *
   * Defaults to user prop passed to <AppBarLayout>
   */
  user?: UserForAppBar | undefined;
} & Partial<AccountMenuItemButtonProps>) {
  const accountMenuContext = useContext(AccountMenuContext)!;

  // This was originally a simple component without context or a user prop, but app
  // developers were rendering it incorrectly. The context and hardcoded logout method and
  // URL makes it more plug&play.
  const user = userProp ?? accountMenuContext.user;

  const handleClick = useCallback(() => {
    logout({
      logoutParams: {
        // Return to Neon splash page
        returnTo: `${window.location.origin}/loggedout`,
      },
    });
  }, [logout]);

  if (!user) {
    return null;
  }

  return (
    <AccountMenuItemButton icon={<SignOut />} onClick={handleClick} {...rest}>
      Sign out
    </AccountMenuItemButton>
  );
}
